import {
  Bundle,
  BundleLine,
  BundleLineId,
  BundleLineVariantId,
  deepCopyBundle,
  getPrice,
  ProductId,
  Quantity,
  SpecialPricePeriod,
  unwrapPrice,
} from "@greeter/core";
import {
  BundleForm,
  BundleLineForm,
  QuantityPicks,
  VariantPicks,
} from "../../models";
import { indexBy } from "@greeter/map";
import { indexBundleLineVariants } from "./indexing";
import { Log } from "@greeter/log";

export function bundleHasChanges(a: Bundle, b: Bundle) {
  const aVariants = a.lines
    .map((l) => l.variants)
    .flat()
    .filter((v) => (v.quantity ?? 0) > 0)
    .sort((a, b) => (a > b ? 1 : 0));
  const bVariants = b.lines
    .map((l) => l.variants)
    .flat()
    .filter((v) => (v.quantity ?? 0) > 0)
    .sort((a, b) => (a > b ? 1 : 0));

  if (aVariants.length !== bVariants.length) return true;
  for (let i = 0; i < aVariants.length; i++) {
    const aVariant = aVariants[i];
    const bVariant = bVariants[i];

    if (aVariant.id !== bVariant.id) return true;
    if (aVariant.quantity !== bVariant.quantity) return true;
  }
  return false;
}

export function bundleIsValid(bundle: Bundle): boolean {
  for (const line of bundle.lines) {
    const lineQuantity = line.quantity;
    let variantQuantity = 0;

    for (const variant of line.variants) {
      variantQuantity += variant.quantity ?? 0;

      if (variantQuantity > lineQuantity) return false;
    }

    if (variantQuantity !== lineQuantity) return false;
  }

  return true;
}

export function bundlePicks(bundle: BundleForm): VariantPicks {
  return bundle.lines.reduce((lineAcc, lineCurr) => {
    lineAcc.set(
      lineCurr.id,
      lineCurr.variants.reduce((variantAcc, variantCurr) => {
        if (variantCurr.quantity && variantCurr.quantity > 0) {
          const existing = variantAcc.get(variantCurr.id) ?? [];
          existing.push({
            type: "normal",
            quantity: variantCurr.quantity,
            additionalPrice: variantCurr.additionalPrice,
          });
          variantAcc.set(variantCurr.id, existing);
        }
        return variantAcc;
      }, new Map() as QuantityPicks)
    );
    return lineAcc;
  }, new Map() as VariantPicks);
}

type UpsellItem = {
  quantity: Quantity;
  bundleLineVariantId: BundleLineVariantId;
  bundleLineId: BundleLineId;
};
type UpsellItems = Map<ProductId, UpsellItem>;
export function bundleUpsells(bundle: BundleForm): UpsellItems {
  const indexedVariants = indexBundleLineVariants([bundle]);

  return bundle.lines
    .map((l) => l.variants.map((v) => ({ bundleLineId: l.id, variant: v })))
    .flat()
    .reduce((acc, curr) => {
      const productId = indexedVariants.get(curr.variant.id)?.productId;
      if (!productId) return acc;
      const upsellItem: UpsellItem = acc.get(productId) ?? {
        quantity: 0,
        bundleLineId: curr.bundleLineId,
        bundleLineVariantId: curr.variant.id,
      };
      upsellItem.quantity += curr.variant.quantity ?? 0;
      acc.set(productId, upsellItem);
      return acc;
    }, new Map() as UpsellItems);
}

export function bundleApplyPicks(bundle: Bundle, picks: VariantPicks): Bundle {
  const bundleCopy = deepCopyBundle(bundle);
  const variantsAsMap = indexBy(
    bundleCopy.lines.map((l) => l.variants).flat(),
    (v) => v.id
  );
  for (const [_, variantLine] of variantsAsMap) {
    variantLine.quantity = 0;
  }
  for (const [_, linePicks] of picks) {
    for (const [variantLineId, pick] of linePicks) {
      const variantLine = variantsAsMap.get(variantLineId);
      if (!variantLine) continue;
      variantLine.quantity = pick.reduce((acc, curr) => acc + curr.quantity, 0);
    }
  }
  return bundleCopy;
}

export function bundleLineLocked(bundleLine: BundleLine) {
  return bundleLine.variants.length === 1;
}

export function bundleLinePickedQuantity(bundleLine: BundleLineForm) {
  return Math.max(
    bundleLine.variants.reduce((acc, curr) => acc + (curr.quantity ?? 0), 0),
    0
  );
}

export function bundleLineExtraQuantity(
  bundleLine: BundleLineForm,
  id?: BundleLineVariantId
) {
  return Math.max(
    bundleLine.extra?.reduce(
      (acc, curr) =>
        acc +
        (id && curr.bundleLineVariantId === id
          ? curr.quantity
          : !id
          ? curr.quantity
          : 0),
      0
    ) ?? 0,
    0
  );
}

export function bundleLineIsAtLimit(bundleLine: BundleLineForm) {
  const maxQuantity = bundleLine.quantity;
  const _pickedQuantity = bundleLinePickedQuantity(bundleLine);
  /**
   * We moving into "extra" territory
   */
  return _pickedQuantity === maxQuantity;
}

export function bundleLineLimitReached(bundleLine: BundleLineForm) {
  const maxQuantity = bundleLine.quantity;
  const _pickedQuantity = bundleLinePickedQuantity(bundleLine);
  /**
   * We moving into "upsell" territory
   */
  return _pickedQuantity >= maxQuantity;
}

export function bundleLineLimitReachedExactly(bundleLine: BundleLineForm) {
  const maxQuantity = bundleLine.quantity;
  const _pickedQuantity = bundleLinePickedQuantity(bundleLine);
  return _pickedQuantity === maxQuantity;
}

export function bundleLineLimitExceeded(bundleLine: BundleLineForm) {
  const maxQuantity = bundleLine.quantity;
  const _pickedQuantity =
    bundleLinePickedQuantity(bundleLine) + bundleLineExtraQuantity(bundleLine);
  return _pickedQuantity > maxQuantity;
}

export function bundleLineLimitExceededBy(bundleLine: BundleLineForm) {
  const maxQuantity = bundleLine.quantity;
  const _pickedQuantity =
    bundleLinePickedQuantity(bundleLine) + bundleLineExtraQuantity(bundleLine);
  return _pickedQuantity - maxQuantity;
}

export function bundleLineExtraPrice(bundleLine: BundleLineForm) {
  let result = 0;
  const variants = indexBy(bundleLine.variants, (v) => v.id);
  for (const extra of bundleLine.extra) {
    const variant = variants.get(extra.bundleLineVariantId!);
    if (!variant) continue;
    result += extra.quantity * variant.salesPrice;
  }
  return result;
}

export function bundleLineAdditionalPrice(bundleLine: BundleLine) {
  let result = 0;
  for (const variant of bundleLine.variants) {
    result += variant.additionalPrice * (variant.quantity ?? 0);
  }
  return result;
}

export function bundleSumAdditionalPrice(bundle: Bundle) {
  return bundle.lines.reduce(
    (acc, curr) => bundleLineAdditionalPrice(curr) + acc,
    0
  );
}

export function bundleSumExtraPrice(bundle: BundleForm) {
  let result = 0;
  for (const line of bundle.lines) {
    result += bundleLineExtraPrice(line);
  }
  return result;
}

export function bundleSumTotalPrice(
  bundle?: BundleForm,
  opts?: { specialPricePeriods: Array<SpecialPricePeriod>; forDate?: Date }
) {
  if (!bundle) return 0;
  const price = unwrapPrice(getPrice(bundle, opts));
  const extraPrice = bundleSumExtraPrice(bundle);
  const additionalPrice = bundleSumAdditionalPrice(bundle);
  return price + extraPrice + additionalPrice;
}
